import { useState } from "react";
import { useTranslation } from "react-i18next";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";
import { Button } from "@mui/material";

import { useClientFileApi } from "../../../api/ClientFileApiContext";
import { useOptionalClaimSelection } from "../../context/ClaimSelectionContext";
import { CarryForwardBalancesDialog } from "../balance-carry-forward/CarryForwardBalancesDialog";
import { BalanceSurchargeDialog } from "../balance-surcharge/BalanceSurchargeDialog";
import { ClaimCreationDialog } from "../create-claim/ClaimCreationDialog";

interface Props {
  clientId: string;
}

export function ClaimActionButtons({ clientId }: Props) {
  const { t } = useTranslation();
  const claimsApi = useClientFileApi().claims;
  const selection = useOptionalClaimSelection();
  // Both bulk actions are staff-only, so their endpoints are absent from the portal's API surface —
  // and both act on the list's selection, which only the staff app mounts.
  const canApplySurcharge = claimsApi?.applyClaimBalanceSurcharge !== undefined && selection !== null;
  const canCarryForward = claimsApi?.carryForwardClaimBalances !== undefined && selection !== null;
  const selectedCount = selection?.selectedClaimIds.size ?? 0;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSurchargeDialogOpen, setIsSurchargeDialogOpen] = useState(false);
  const [isCarryForwardDialogOpen, setIsCarryForwardDialogOpen] = useState(false);

  return (
    <>
      {canCarryForward && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<UpdateRoundedIcon />}
          disabled={selectedCount === 0}
          onClick={() => setIsCarryForwardDialogOpen(true)}
        >
          {t("claims.toolbar.carryForward")}
        </Button>
      )}
      {canApplySurcharge && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<PercentRoundedIcon />}
          disabled={selectedCount === 0}
          onClick={() => setIsSurchargeDialogOpen(true)}
        >
          {t("claims.toolbar.applySurcharge")}
        </Button>
      )}
      <Button
        variant="contained"
        size="small"
        startIcon={<AddRoundedIcon />}
        onClick={() => setIsCreateDialogOpen(true)}
      >
        {t("claims.toolbar.newClaim")}
      </Button>
      {isCreateDialogOpen && <ClaimCreationDialog clientId={clientId} onClose={() => setIsCreateDialogOpen(false)} />}
      {isSurchargeDialogOpen && (
        <BalanceSurchargeDialog clientId={clientId} onClose={() => setIsSurchargeDialogOpen(false)} />
      )}
      {isCarryForwardDialogOpen && (
        <CarryForwardBalancesDialog clientId={clientId} onClose={() => setIsCarryForwardDialogOpen(false)} />
      )}
    </>
  );
}
